import PlaceNotFoundError from '@/entities/errors/PlaceNotFoundError';
import { ContentType } from '@/entities/types/HttpContentType';
import { HttpError } from '@/entities/types/HttpError';
import { Logger } from '@/logger';
const defaultConfig = {
    timeout: 10000,
    retries: 3,
};
async function request(url, options) {
    const res = await fetch(url, options).catch((error) => {
        console.error(`Error while fetching ${url}`, error);
        throw new PlaceNotFoundError(url, error);
        // throw new HttpError({ status: 404 } as Response);
    });
    if (res.status >= 400) {
        // console.info(`${options.method} request to ${url} got a ${res.status} status code`);
        Logger.info(`${options.method} request to ${url} got a ${res.status} status code`, {
            headers: res.headers,
        });
        throw new HttpError(res);
    }
    return res;
}
async function abortableRequest(url, options, { timeout, retries, ...rest }) {
    retries--;
    try {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        const result = await request(url, {
            ...options,
            signal: controller.signal,
        });
        clearTimeout(id);
        return result;
    }
    catch (error) {
        if (retries <= 0 || (error instanceof HttpError && error.status < 500)) {
            throw error;
        }
        return abortableRequest(url, options, { timeout, retries, ...rest });
    }
}
class HttpClient {
    static instance;
    request;
    constructor() {
        try {
            // Some browsers don't support AbortController, use a fallback with non retried and timeouted request
            new AbortController();
            this.request = abortableRequest;
        }
        catch {
            this.request = request;
        }
    }
    /**
     *
     * @returns A singleton instance of the HttpClient
     *
     * @public
     */
    static getInstance() {
        if (!HttpClient.instance) {
            HttpClient.instance = new HttpClient();
        }
        return HttpClient.instance;
    }
    /**
     * Perform a GET call with JSON content-type
     *
     * @param url - The url to get
     * @param config
     * @param apiKey
     * @returns A reponse cast to the given `T` type
     *
     * @throws `HttpError`
     * This error is raised if the request response has a status >= 400
     *
     * @public
     */
    async get(url, config, apiKey) {
        const res = await this.getRaw(url, ContentType.JSON, config, apiKey);
        return res.json();
    }
    /**
     * Perform a GET call
     *
     * @param url - The url to get
     * @param contentType - The accepted content type
     * @param config
     * @param apiKey
     * @returns A response
     *
     * @throws `HttpError`
     * This error is raised if the request response has a status >= 400
     *
     * @public
     */
    async getRaw(url, contentType, config, apiKey) {
        const options = {
            headers: {
                Accept: contentType,
                ...(apiKey && { 'X-Api-Key': apiKey }),
            },
            method: 'get',
        };
        return this.request(url, options, config ? config : defaultConfig);
    }
}
export default HttpClient;
